/**
 * Filtro da paleta de comandos (I1) — puro, testável sem DOM.
 *
 * Regra: cada palavra digitada precisa aparecer no rótulo, na descrição ou nas palavras-
 * chave do item, sem diferenciar maiúsculas nem acento ("promocao" acha "Promoção").
 * Ordem: quem COMEÇA com o que foi digitado vem antes; empate mantém a ordem original
 * (a lista já chega na ordem que a tela escolheu).
 */

export interface FilterableCommand {
  label: string;
  description?: string;
  keywords?: readonly string[];
}

export interface FilterableGroup<T extends FilterableCommand> {
  id: string;
  items: readonly T[];
}

export function normalizeSearch(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function haystack(item: FilterableCommand): string {
  return normalizeSearch([item.label, item.description ?? "", ...(item.keywords ?? [])].join(" "));
}

export function matchesCommand(item: FilterableCommand, query: string): boolean {
  const q = normalizeSearch(query);
  if (q.length === 0) return true;
  const text = haystack(item);
  return q.split(" ").every((token) => text.includes(token));
}

export interface FilterOptions {
  /** Teto por grupo enquanto a busca está vazia. */
  emptyLimit?: number;
  /** Teto por grupo com busca. */
  queryLimit?: number;
}

export function filterCommandGroups<G extends FilterableGroup<FilterableCommand>>(
  groups: readonly G[],
  query: string,
  { emptyLimit = 6, queryLimit = 8 }: FilterOptions = {},
): Array<Omit<G, "items"> & { items: Array<G["items"][number]> }> {
  const q = normalizeSearch(query);
  const limit = q.length === 0 ? emptyLimit : queryLimit;
  return groups
    .map((group) => {
      const matched = group.items
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => matchesCommand(item, q))
        .sort((a, b) => {
          if (q.length === 0) return a.index - b.index;
          const aStarts = normalizeSearch(a.item.label).startsWith(q) ? 0 : 1;
          const bStarts = normalizeSearch(b.item.label).startsWith(q) ? 0 : 1;
          return aStarts - bStarts || a.index - b.index;
        })
        .slice(0, limit)
        .map(({ item }) => item);
      return { ...group, items: matched };
    })
    .filter((group) => group.items.length > 0);
}
